import { computed, type ComputedRef } from "vue";
import { useGlobalDark } from "@mkbabb/glass-ui/dark";
import { deriveAurora, type AuroraConfig } from "@mkbabb/glass-ui/aurora";
import { useSpeedtestStore } from "@features/speedtest";

/**
 * Speedtest aurora preset.
 *
 * Six-hue palette mapped to six nuclei distributed across the canvas
 * produces a soft, drifting gradient backdrop. OKLCH values approximate
 * the `--aurora-1..6` hex tokens in styles/tokens.css:
 *   purple, blue, pink, emerald, amber, violet.
 *
 * Light/dark fork: alpha drops in dark mode so the backdrop sits softer
 * against the panel surface; drift knobs lift so motion reads (the
 * pre-N values were near zero and the canvas appeared static).
 *
 * ─── DDR: AN-D10 — REVERSAL of AJ.W2-α "wash" aurora desaturation ───────────
 * WHAT: Restored C (chroma) values to pre-AJ register: purple/pink C 0.22,
 *   blue C 0.14, emerald C 0.16, amber C 0.18, violet C 0.18. Restored
 *   warpAmount 0.30 → 0.50 (within documented 0..0.6 range). Restored
 *   saturation 0.55 → 0.85. Restored valueVariance 0.05 → 0.08. Lifted
 *   all three drift knobs +67% off the AJ.W1-ε floor (warpDrift 0.012 →
 *   0.020, nucleiDrift 0.024 → 0.040, paletteDrift 0.012 → 0.020) while
 *   PRESERVING the deliberate 1:2:1 asymmetric cadence ratio (AJ.W4-λ).
 * WHY: D10 directive — "Aurora BG is just diagonal rainbow streaks". The
 *   AJ wash overshot to muddy, static bands. Without warpAmount the FBM
 *   noise field cannot deform the colour bands, so the palette-banded
 *   cells read as straight diagonal stripes (exactly what D10 observed).
 *   At saturation 0.55 the six hues collapse into indistinct pastels;
 *   the DESIGN.md register targets vibrant, distinct hues. Together the
 *   low warp + low saturation + near-floor drift created a degraded,
 *   non-atmospheric backdrop instead of the documented whispy atmosphere.
 * ALTERNATIVE REJECTED:
 *   (a) Keep muted streaks — directly contradicted by D10.
 *   (b) glass-ui root rewrite of the Aurora primitive — R0 prelude
 *       confirmed config-only suffices; no library edit required.
 * RE-LITIGATE-IF: perf regression on low-end GPUs (warpAmount drives
 *   FBM cost); or chassis dissolving into aurora at idle (watch ALPHA_*
 *   constants and auroraOpacityCeiling — both honored, see below).
 * HONORED — NOT reversed by this DDR:
 *   • AK-W6-α opacity-ceiling DDR: auroraOpacityCeiling ramp (1.0 dial
 *     route, 0.5 survey/thankyou/admin) lives in App.vue — untouched.
 *   • Q.W1.B running-α drop: ALPHA_LIGHT_RUNNING / ALPHA_DARK_RUNNING
 *     constants remain as-is — the foreground legibility guard holds.
 *   • AJ.W4-λ cadence-asymmetry: 1:2:1 drift ratio preserved at the new
 *     (higher) floor — nuclei still orbit 2× faster than warp/palette.
 * MEASUREMENT NOTE (AO W-RATCHET-A2 → AP CARRY-AO-3): the original AN close
 *   stated a "46% pixel-diff over 1.5s on idle" as the dynamism proof. A
 *   re-measure on integrated master found 7.23% full-frame / 1.63%
 *   aurora-only over 1.5s, and 7.98% aurora-only over 5s — the AN figure was
 *   methodologically under-witnessed (the home route auto-starts a live run
 *   on mount; the original capture pair most likely fell inside that
 *   auto-start window, where the chassis sweep dominates per-frame deltas).
 *   The architectural restoration here is verified; the honest characterization
 *   is a SLOW-PAINTERLY drift (~15–18s subjective period; ≥7% aurora-only at
 *   5s) — alive at the design-intended tempo, not a 46%/1.5s churn. See
 *   docs/audits/2026-05-28-AO-W-RATCHET (A2) + [[feedback_persisted_evidence]].
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── DDR-AV-W12-DERIVE: the AS-6 pale-recede palette retires onto glass-ui's
//     shipped `deriveAurora` root (DDR-AS-RC-2 / §8-H10 promise honored) ──────
// WHAT: the hand-maintained 6-stop OKLCH `PALETTE` array (the AS-6 "consumer
//   stopgap" pending the GU root) is gone; the palette is now DERIVED from a
//   compact `{seed, options}` intent via `deriveAurora` (`@mkbabb/glass-ui/aurora`).
//   The derived ramp lands the IDENTICAL pale-pastel REGISTER the AS-6 stopgap
//   hand-tuned (L≈0.89–0.92, C≈0.05–0.06, a tight lilac→periwinkle→violet→rose
//   analogous walk that dissolves into one pale family — see the seed below).
// WHY: AS-6's own marker named this: "The GU root (`deriveAurora` + OKLab LUT)
//   supersedes this stopgap at the next consume." `deriveAurora` ships in 3.10.0
//   and CAN express the recede — the pale-recede register is exactly its
//   `{high-L seed, low chromaFalloff, small lightnessSpread}` basin (verified:
//   the derived stops reproduce the AS-6 L/C family). The band-dissolve was
//   never the palette hand-tuning — it is the shader trio that STAYS: `softmaxBeta`
//   DOWN (exp(-β·d²/r²) softens the per-nucleus hand-off), `warpAmount` 0.50 (FBM
//   deform), and the COMPRESSED `paletteBias` gamut on the nuclei (fewer pure-hue
//   corners). So this is a genuine revert-onto-shipped-root, NOT a DDR-keep of the
//   stopgap. The `#CC2233` dial stays vibrant on its own layer — the aurora is the
//   recessive wash behind it.
// ALTERNATIVE REJECTED: (a) keep the hand-rolled stop-array — contradicts the
//   AS-6 supersede-at-next-consume promise + §8-H10. (b) DDR-keep the stopgap as
//   "deriveAurora cannot express the recede" — falsified: the derived register
//   matches. The aurora-generalization seam (App.vue `<Aurora>` mount / WebGL) is
//   W16 territory, untouched here.
// RE-LITIGATE-IF: a future glass-ui `deriveAurora` change shifts the pale basin
//   (re-pin the seed/options); or the π-lane occlusion gate flags an UNINTENDED
//   field shift (then re-seat the seed or route the residual to W16).
// ────────────────────────────────────────────────────────────────────────────
//
// The compact intent: one warm-pale lilac seed → a 6-stop analogous pale family.
// `chromaFalloff` 0.95 + `lightnessSpread` 0.04 keep every stop in the recede
// register; `temperatureShift` 0.15 warms the apex toward the AS-6 cream note.
const PALETTE: AuroraConfig["palette"] = deriveAurora(
    { L: 0.91, C: 0.07, h: 300 },
    {
        stopCount: 6,
        harmony: "analogous",
        lightnessSpread: 0.04,
        chromaFalloff: 0.95,
        hueSpread: 65,
        temperatureShift: 0.15,
    },
);

// `paletteBias` gamut COMPRESSED (extremes 0.0/1.0 pulled to 0.12/0.88)
// so no nucleus samples a pure-hue palette corner — the field reads as one
// pale family rather than six countable rainbow blobs. The top-centre nucleus
// gets a cream `valueBias` lift (the warm apex).
const NUCLEI: AuroraConfig["nuclei"] = [
    {
        x: 0.18,
        y: 0.22,
        radius: 0.55,
        paletteBias: 0.12,
        valueBias: 0.04,
        driftRadius: 0.02,
        driftPhase: 0.3,
    },
    {
        x: 0.8,
        y: 0.3,
        radius: 0.52,
        paletteBias: 0.2,
        valueBias: 0.0,
        driftRadius: 0.02,
        driftPhase: 1.7,
    },
    {
        x: 0.5,
        y: 0.18,
        radius: 0.48,
        paletteBias: 0.4,
        valueBias: 0.06,
        driftRadius: 0.02,
        driftPhase: 3.4,
    },
    {
        x: 0.72,
        y: 0.78,
        radius: 0.55,
        paletteBias: 0.6,
        valueBias: -0.02,
        driftRadius: 0.018,
        driftPhase: 0.9,
    },
    {
        x: 0.22,
        y: 0.82,
        radius: 0.5,
        paletteBias: 0.8,
        valueBias: 0.02,
        driftRadius: 0.022,
        driftPhase: 2.1,
    },
    {
        x: 0.55,
        y: 0.58,
        radius: 0.58,
        paletteBias: 0.88,
        valueBias: 0.0,
        driftRadius: 0.015,
        driftPhase: 4.6,
    },
];

/** Static portion of the speedtest preset — every knob except alpha. */
const STATIC: Omit<AuroraConfig, "alpha"> = {
    palette: PALETTE,
    nuclei: NUCLEI,
    softmaxBeta: 2.8, // AS-6 — DOWN from 3.2: lower β softens the per-nucleus hand-off (shader exp(-β·d²/r²)) so the bands dissolve into one wash (§8-H4). This is the anti-muddy lever, NOT chroma.
    valueVariance: 0.08, // AN-D10 restore (AJ.W2-α wash had 0.05; was 0.08 pre-AJ)
    warpAmount: 0.5, // AN-D10 restore — whispy FBM warp (AJ.W2-α wash had 0.30; documented target ~0.5)
    warpScale: 1.6,
    // AI.W4-M.3 (D27 Q2 ratified, proposal #25) — aurora drift cadence
    // harmonisation. The three drift knobs (`warpDrift`, `nucleiDrift`,
    // `paletteDrift`) collectively shape the perceived drift period;
    // halving each one roughly doubles the period. The pre-W4 values
    // (warpDrift 0.02, nucleiDrift 0.04, paletteDrift 0.02) read as a
    // ~12s subjective drift cycle that beat against the 6s ambient
    // canon (the dial breath + the Pulse aura tokens both ride 6s).
    // Per FD2 §6 rule-1 "Multiple ambient cycles on adjacent elements
    // should SHARE a period multiple so they don't beat against each
    // other", I slow each drift to roughly half its prior speed so
    // the resulting ~24-30s drift period is a clean multiple of 6s.
    // The aurora reads as a slower, more painterly atmosphere rather
    // than as a competing rhythm.
    //
    // AJ.W1-ε (G-AJ-D7 + G-AJ-D14) — drift-floor restoration. The
    // post-W4 0.01 was perceptually static; the GL FBM noise field
    // stalled and the palette-banded cells read as diagonal lines
    // (live regression P0-4). I lift the floor to 0.012 (+20%) — still
    // a clean multiple of the 6s ambient canon (~20-25s subjective
    // period; well inside the FD2 §6 rule-1 basin), but clear of the
    // perceptual stall point. Hypothesis B confirmed at
    // docs/tranches/AJ/W1-spec/W1-epsilon-diagnostic/VERDICT.md.
    //
    // AN-D10 — whispy/dynamic restore. Lifted +67% above the AJ.W1-ε
    // floor (0.012 → 0.020) so the warp field visibly flows. Still a
    // clean multiple of the 6s canon (~15-18s subjective period). The
    // 1:2:1 asymmetric ratio (AJ.W4-λ) is preserved: warpDrift 0.020 :
    // nucleiDrift 0.040 : paletteDrift 0.020.
    warpDrift: 0.02,
    warpMode: "fbm",
    noiseOctaves: 4,
    medium: "smooth",
    flow: { pattern: "none", focalX: 0.5, focalY: 0.5, angle: 0, curl: 0 },
    strokeAmount: 0,
    strokeScale: 140,
    strokeAnisotropy: 0.7,
    strokeLayers: 1,
    strokeMode: "oil",
    wetEdge: 0,
    granulation: 0,
    impasto: 0,
    brokenColor: 0,
    canvasGrain: 0,
    // AI.W4-M.3 (D27 Q2) — see the `warpDrift` comment above; the
    // pre-W4 0.04 / 0.02 ride at ~12s subjective; halving to 0.02 /
    // 0.01 brings the cycle out to ~24-30s, a clean multiple of 6s.
    // AJ.W1-ε — drift-floor restoration paired with `warpDrift`
    // above. Holds the W4 2× ratio (nuclei orbit faster than the
    // field warps); both knobs lifted +20% off the perceptual stall
    // floor.
    //
    // AJ.W4-λ — cadence-asymmetry note. The three drift knobs hold a
    // deliberate 1:2:1 ratio (warpDrift : nucleiDrift : paletteDrift).
    // The nuclei orbit at 2× the field-warp / palette-drift rate so the
    // aurora reads as a SLOW TONAL WALK (palette + warp slowly migrating;
    // nuclei tracking ahead). A 1:1:1 harmonisation would collapse those
    // three motions into one uniform pan — the W4-M.3 + W1-ε settlement
    // explicitly preferred the asymmetric register over symmetric harmony
    // (per A5 §8.10). The 1:2:1 ratio is INTENTIONAL; do not "fix"
    // toward 1:1:1 without re-litigating that design call.
    //
    // AN-D10 — all three lifted +67% above AJ.W1-ε floor while
    // preserving the ratio: 0.020 : 0.040 : 0.020 (was 0.012 : 0.024 :
    // 0.012). The aurora now visibly drifts and breathes.
    nucleiDrift: 0.04,
    paletteDrift: 0.02,
    breathDepth: 0.08,
    breathPeriod: 42,
    saturation: 0.62, // AS-6 (DDR REVERSAL of AN-D10's 0.85) — pastel-recede; the un-muddiness comes from softmaxBeta↓ + warp 0.50 + bias-compress, not chroma
    paperGrain: 0,
};

// Idle/complete baselines: the canvas reads as soft atmosphere behind
// freshly-feathered chrome. Dark runs softer than light.
// AJ.W2-α wash — drop ~30% per FD1 §5.2 (was 0.26 / 0.18). The Aurora is
// backdrop, not protagonist; the lower alpha lets the chassis card +
// dial read clean against a softer tinted volume.
//
// AV.W16 (M3-R6 light-parity amend) — ALPHA_LIGHT lowered 0.18 → 0.13. On the
// LIGHT register the flooded card sits over a near-white ground, so the same
// alpha that reads as recessive on dark floods the light card and erodes the
// #CC2233 readout's contrast against the wash. 0.13 holds the WCAG-AA-large
// floor for the #CC2233 / gold metric readout against the flooded card
// (witnessed `w16/aurora-light.png` + `w16/contrast.md`) while the wash stays
// PRESENT (not receded — the scope-8 presence ceiling is a SEPARATE axis the
// opacityCeiling reversal governs; this is the contrast FLOOR only). Dark is
// unchanged at 0.14 — the dark ground already gives the readout its contrast.
const ALPHA_LIGHT = 0.13;
const ALPHA_DARK = 0.14;

// During multi-second running phases the aurora steps back so the
// results panel stays legible against saturated regions (live reads
// showed the foreground dissolving into the aurora at ~70% subjective
// transparency at the higher alpha). Same proportional drop on each
// fork.
// AJ.W2-α wash — drop ~33% per FD1 §5.2 (was 0.18 / 0.12). The running
// register sits deeper still so the foreground panels stay legible.
const ALPHA_LIGHT_RUNNING = 0.12;
const ALPHA_DARK_RUNNING = 0.08;

/**
 * Reactive speedtest preset.
 *
 * Returns a `ComputedRef<AuroraConfig>` that re-emits when `useGlobalDark`
 * flips OR when the speedtest phase crosses the ready/running boundary.
 * The canvas's `useAurora` deep-watches the config getter, so the alpha
 * fork propagates without a manual rebind.
 */
export function useSpeedtestAuroraConfig(): ComputedRef<AuroraConfig> {
    const { isDark } = useGlobalDark();
    const speedtest = useSpeedtestStore();
    return computed<AuroraConfig>(() => {
        const dark = isDark.value;
        // currentPhase resolves to "ready" / "ping" / "download" / "upload" /
        // "complete"; only the active running phases drop the alpha so the
        // at-rest aurora bleed stays at the codified target.
        const phase = speedtest.currentPhase;
        const running =
            phase === "ping" || phase === "download" || phase === "upload";
        const alpha = running
            ? dark
                ? ALPHA_DARK_RUNNING
                : ALPHA_LIGHT_RUNNING
            : dark
              ? ALPHA_DARK
              : ALPHA_LIGHT;
        return { ...STATIC, alpha };
    });
}

/**
 * Static fallback preset. Retained for tests that need a non-reactive
 * shape (App-mount tests stub `useGlobalDark` indirectly via matchMedia)
 * and for any consumer that does not want the dark-mode fork.
 */
export const SPEEDTEST_AURORA_CONFIG: AuroraConfig = {
    ...STATIC,
    alpha: ALPHA_LIGHT,
};
